import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  Signal,
  signal,
  TemplateRef,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableModule,
} from '@angular/material/table';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { TableStateService } from '@core/services/table-state.service';
import { EEventType, TEventFrontendTab } from '@enums/event-tracking.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { GroupedSkeletonLoaderComponent } from '@shared/components/skeleton-loader/grouped-skeleton-loader.component';
import { IGroupedSkeletonLoaderConfig } from '@shared/components/skeleton-loader/grouped-skeleton-loader.interface';
import { map, startWith } from 'rxjs';
import {
  IGroupedDatatableChildRowClickEvent,
  IGroupedDatatableColumn,
  IGroupedDatatableConfiguration,
  IGroupedDatatableCustomColumn,
  IGroupedDatatableExpandChange,
  IGroupedDatatableFilterChange,
  IGroupedDatatableGroup,
  IGroupedDatatablePageChange,
  IGroupedDatatableParentRowClickEvent,
} from './grouped-datatable.interface';

interface IColumnWithTemplate<TRow> extends IGroupedDatatableColumn<TRow> {
  template: TemplateRef<unknown> | null | undefined;
}

interface IGroupedRenderRow<TParent, TChild, idType extends number | string> {
  children: TChild[];
  id: idType;
  parent: TParent;
}

@Component({
  imports: [
    IconComponent,
    MatButtonModule,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIconButton,
    MatIconModule,
    MatPaginator,
    MatPaginatorModule,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatSortModule,
    MatTable,
    MatTableModule,
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    GroupedSkeletonLoaderComponent,
    TranslateModule,
  ],
  selector: 'app-grouped-datatable',
  styleUrl: './grouped-datatable.component.scss',
  templateUrl: './grouped-datatable.component.html',
})
export class GroupedDatatableComponent<
  TParent extends { id: idType },
  TChild extends { id: number | string },
  idType extends number | string = number,
> {
  public readonly groups = input.required<IGroupedDatatableGroup<TParent, TChild, idType>[]>();
  public readonly parentColumns = input.required<IGroupedDatatableColumn<TParent>[]>();
  public readonly totalParentCount = input.required<number>();

  public readonly childColumns = input<IGroupedDatatableColumn<TChild>[]>([]);
  public readonly configurationInput = input<IGroupedDatatableConfiguration<TParent, TChild>>({});
  public readonly customChildColumnDef = input<IGroupedDatatableCustomColumn<TChild>[]>([]);
  public readonly customParentColumnDef = input<IGroupedDatatableCustomColumn<TParent>[]>([]);
  public readonly isInitialDataLoaded = input<boolean>(false);
  public readonly loaderConfig = input<IGroupedSkeletonLoaderConfig | null>(null);
  public readonly loadingData = input<boolean>(false);
  /** For campaign_analytics_content_group_expanded/collapsed/all_expanded/all_collapsed — which tab this instance is rendered in. Omit if not used in that context. */
  public readonly currentTab = input<TEventFrontendTab>();

  public readonly childRowClick = output<IGroupedDatatableChildRowClickEvent<TParent, TChild>>();
  public readonly expandChange = output<IGroupedDatatableExpandChange<idType>>();
  public readonly filterChange = output<IGroupedDatatableFilterChange>();
  public readonly pageChange = output<IGroupedDatatablePageChange>();
  public readonly parentRowClick = output<IGroupedDatatableParentRowClickEvent<TParent>>();
  public readonly sortChange = output<Sort>();

  public readonly searchControl = new FormControl<string>('', { nonNullable: true });
  public readonly searchTerm = toSignal(
    this.searchControl.valueChanges.pipe(
      map((value) => value.trim().toLowerCase()),
      startWith(''),
    ),
    { initialValue: '' },
  );

  public readonly configuration: Signal<Required<IGroupedDatatableConfiguration<TParent, TChild>>> = computed(() => {
    const configurationInput = this.configurationInput();

    return {
      childFilterPredicate: (child: TChild, searchTerm: string) =>
        this.matchesSearchTerm(child, searchTerm, this.childColumns()),
      childIndentPx: 24,
      defaultSort: { active: '', direction: '' },
      hideParentsWithoutVisibleChildren: false,
      isChildHeaderEnabled: false,
      isPaginationEnabled: true,
      isSearchEnabled: true,
      pageSizeOptions: [5, 10, 25, 50, 100],
      parentFilterPredicate: (parent: TParent, searchTerm: string) =>
        this.matchesSearchTerm(parent, searchTerm, this.parentColumns()),
      parentSortAccessor: (parent: TParent, sortKey: string) => this.defaultSortAccessor(parent, sortKey),
      preserveExpandStateOnFilter: true,
      rowSeparatorEnabled: true,
      showExpandAllToggle: true,
      storageKey: null,
      ...configurationInput,
      isChildRowClickEnabled: configurationInput.isChildRowClickEnabled ?? true,
      isParentRowClickEnabled: configurationInput.isParentRowClickEnabled ?? true,
      isParentRowToggleEnabled: configurationInput.isParentRowToggleEnabled ?? true,
    };
  });

  public readonly sortState = computed(() => this.internalSortState());
  public readonly parentColumnsWithTemplate = computed(() => this.mergeColumnsWithTemplate(this.parentColumns(), true));
  public readonly childColumnsWithTemplate = computed(() => this.mergeColumnsWithTemplate(this.childColumns(), false));
  public readonly parentDisplayedColumnIds = computed(() => [
    ...this.parentColumnsWithTemplate().map((column) => column.id),
    'expand',
  ]);
  public readonly isInitialDataLoading = computed(() => !this.isInitialDataLoaded() && this.loadingData());

  public readonly filteredGroups = computed<IGroupedRenderRow<TParent, TChild, idType>[]>(() => {
    const groups = this.groups();
    const { hideParentsWithoutVisibleChildren, parentFilterPredicate, childFilterPredicate } = this.configuration();
    const searchTerm = this.searchTerm();
    const result: IGroupedRenderRow<TParent, TChild, idType>[] = [];

    for (const group of groups) {
      const parentMatches = !searchTerm || parentFilterPredicate(group.parent, searchTerm);
      const children = group.children.filter((child) => !searchTerm || childFilterPredicate(child, searchTerm));

      if (hideParentsWithoutVisibleChildren && !parentMatches && children.length === 0) {
        continue;
      }

      result.push({
        children,
        id: group.id,
        parent: group.parent,
      });
    }

    return result;
  });

  public readonly sortedGroups = computed<IGroupedRenderRow<TParent, TChild, idType>[]>(() => {
    const sort = this.sortState();
    const groups = [...this.filteredGroups()];

    if (!sort.active || !sort.direction) {
      return groups;
    }

    const directionMultiplier = sort.direction === 'asc' ? 1 : -1;

    groups.sort((left, right) => {
      const leftValue = this.configuration().parentSortAccessor(left.parent, sort.active);
      const rightValue = this.configuration().parentSortAccessor(right.parent, sort.active);

      return this.compareSortValues(leftValue, rightValue) * directionMultiplier;
    });

    return groups;
  });

  public readonly pagedGroups = computed<IGroupedRenderRow<TParent, TChild, idType>[]>(() => {
    const groups = this.sortedGroups();
    const pageState = this.pageState();

    if (!this.configuration().isPaginationEnabled) {
      return groups;
    }

    const start = pageState.pageIndex * pageState.pageSize;
    const end = start + pageState.pageSize;

    return groups.slice(start, end);
  });

  public readonly allExpanded = computed(() => {
    const groups = this.filteredGroups();

    if (!groups.length) {
      return false;
    }

    return groups.every((group) => this.isGroupExpanded(group.id));
  });

  public readonly paginatorLength = computed(() => this.filteredGroups().length);
  public readonly paginatorState = computed(() => this.pageState());

  private readonly tableStateService = inject(TableStateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly pageState = signal<IGroupedDatatablePageChange>({ pageIndex: 0, pageSize: 5 });
  private readonly internalSortState = signal<Sort>({ active: '', direction: '' });
  private readonly defaultExpandAll = signal<boolean>(true);
  private readonly expansionState = signal<Record<string, boolean>>({});
  private isStateRestored = false;

  constructor() {
    this.setupExpansionSyncEffect();
    this.setupSearchEffect();
    this.setupPageBoundsEffect();
    this.setupStateRestoreEffect();
    this.setupStatePersistEffect();
  }

  public getCellValue<TRow>(column: IColumnWithTemplate<TRow>, row: TRow): Date | number | string | null | undefined {
    if (column.valueGetter) {
      return column.valueGetter(row);
    }

    return (row as Record<string, Date | number | string | null | undefined>)[column.id];
  }

  public getColumnClasses<TRow>(column: IColumnWithTemplate<TRow>): string[] {
    return [...(column.headerClasses ?? [])];
  }

  public getGroupChildren(groupId: idType): TChild[] {
    const group = this.pagedGroups().find((currentGroup) => currentGroup.id === groupId);

    if (!group) {
      return [];
    }

    return group.children;
  }

  public isGroupExpanded(groupId: idType): boolean {
    const key = this.getGroupKey(groupId);
    const expansionState = this.expansionState();

    return expansionState[key] ?? this.defaultExpandAll();
  }

  public onChildRowClicked(event: MouseEvent, parent: TParent, child: TChild): void {
    if (!this.configuration().isChildRowClickEnabled) {
      return;
    }

    this.childRowClick.emit({ child, event, parent });
  }

  public onGroupExpandToggle(groupId: idType, event?: Event): void {
    event?.stopPropagation();

    if (!this.configuration().isParentRowToggleEnabled) {
      return;
    }

    const key = this.getGroupKey(groupId);
    const nextExpandedState = !this.isGroupExpanded(groupId);

    this.expansionState.set({
      ...this.expansionState(),
      [key]: nextExpandedState,
    });

    this.expandChange.emit({ id: groupId, isExpanded: nextExpandedState });

    this.eventTrackingService.track(
      nextExpandedState
        ? EEventType.campaignAnalyticsContentGroupExpanded
        : EEventType.campaignAnalyticsContentGroupCollapsed,
      { currentTab: this.currentTab() },
    );
  }

  public onPageChanged(event: PageEvent): void {
    this.setPageState({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    this.pageChange.emit({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  public onParentRowClicked(event: MouseEvent, parent: TParent): void {
    if (!this.configuration().isParentRowClickEnabled) {
      return;
    }

    this.parentRowClick.emit({ event, parent });
  }

  public onParentRowToggled(event: MouseEvent, groupId: idType, parent: TParent): void {
    if (!this.configuration().isParentRowToggleEnabled) {
      return;
    }

    this.onGroupExpandToggle(groupId);
    this.onParentRowClicked(event, parent);
  }

  public onSortChanged(sort: Sort): void {
    this.setSortState(sort);
    this.sortChange.emit(sort);
  }

  public toggleAllGroups(): void {
    const groups = this.groups();
    const shouldExpand = !this.allExpanded();
    const nextExpansionState: Record<string, boolean> = {};

    this.defaultExpandAll.set(shouldExpand);

    for (const group of groups) {
      const key = this.getGroupKey(group.id);

      nextExpansionState[key] = shouldExpand;
      this.expandChange.emit({ id: group.id, isExpanded: shouldExpand });
    }

    this.expansionState.update((currentState) => ({
      ...currentState,
      ...nextExpansionState,
    }));

    this.eventTrackingService.track(
      shouldExpand
        ? EEventType.campaignAnalyticsAllContentGroupsExpanded
        : EEventType.campaignAnalyticsAllContentGroupsCollapsed,
      { currentTab: this.currentTab() },
    );
  }

  public getChildIndentStyle(): string {
    return `${this.configuration().childIndentPx}px`;
  }

  public isLastPagedGroup(group: { id: idType }): boolean {
    const groups = this.pagedGroups();

    if (groups.length === 0) {
      return true;
    }

    return groups[groups.length - 1]!.id === group.id;
  }

  private setupExpansionSyncEffect(): void {
    effect(() => {
      const groups = this.groups();
      const currentExpansionState = untracked(() => this.expansionState());
      const { preserveExpandStateOnFilter } = this.configuration();
      const nextExpansionState: Record<string, boolean> = {};

      for (const group of groups) {
        const key = this.getGroupKey(group.id);

        if (preserveExpandStateOnFilter && key in currentExpansionState) {
          nextExpansionState[key] = currentExpansionState[key];
        } else {
          nextExpansionState[key] = this.defaultExpandAll();
        }
      }

      if (!this.areExpansionStatesEqual(currentExpansionState, nextExpansionState)) {
        this.expansionState.set(nextExpansionState);
      }
    });
  }

  private setupSearchEffect(): void {
    effect(() => {
      const searchTerm = this.searchTerm();
      const pageIndex = untracked(() => this.pageState().pageIndex);

      this.filterChange.emit({ searchTerm });

      if (pageIndex === 0) {
        return;
      }

      this.setPageState({ pageIndex: 0 });
    });
  }

  private setupPageBoundsEffect(): void {
    effect(() => {
      const filteredGroupLength = this.filteredGroups().length;
      const pageState = this.pageState();
      const maxPageIndex =
        filteredGroupLength > 0 ? Math.max(0, Math.ceil(filteredGroupLength / pageState.pageSize) - 1) : 0;

      if (pageState.pageIndex <= maxPageIndex) {
        return;
      }

      this.setPageState({ pageIndex: maxPageIndex });
    });
  }

  private setupStateRestoreEffect(): void {
    effect(() => {
      const configuration = this.configuration();

      if (this.isStateRestored || !configuration.storageKey) {
        return;
      }

      const state = this.tableStateService.getState(configuration.storageKey);

      if (state) {
        this.setPageState({ pageIndex: state.pageIndex, pageSize: state.pageSize });
        this.setSortState({ active: state.sort.active, direction: state.sort.direction });
      } else if (configuration.defaultSort.active && configuration.defaultSort.direction) {
        this.setSortState(configuration.defaultSort);
      }

      this.isStateRestored = true;
    });
  }

  private setupStatePersistEffect(): void {
    effect(() => {
      const { storageKey } = this.configuration();
      const pageState = this.pageState();
      const sortState = this.sortState();

      if (!storageKey) {
        return;
      }

      this.tableStateService.setState(storageKey, {
        pageIndex: pageState.pageIndex,
        pageSize: pageState.pageSize,
        sort: {
          active: sortState.active,
          direction: sortState.direction as SortDirection,
        },
      });
    });
  }

  private compareSortValues(
    leftValue: Date | number | string | null | undefined,
    rightValue: Date | number | string | null | undefined,
  ): number {
    if (leftValue === rightValue) {
      return 0;
    }

    if (leftValue === null || leftValue === undefined) {
      return -1;
    }

    if (rightValue === null || rightValue === undefined) {
      return 1;
    }

    if (leftValue instanceof Date && rightValue instanceof Date) {
      return leftValue.getTime() - rightValue.getTime();
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return leftValue - rightValue;
    }

    return String(leftValue).localeCompare(String(rightValue));
  }

  private matchesSearchTerm<TValue>(
    value: TValue,
    searchTerm: string,
    columns: IGroupedDatatableColumn<TValue>[],
  ): boolean {
    if (!searchTerm) {
      return true;
    }

    return columns.some((column) => {
      if (column.isSearchable === false) {
        return false;
      }

      const columnValue = this.getFilterValue(column, value);

      if (columnValue === null || columnValue === undefined) {
        return false;
      }

      return String(columnValue).toLowerCase().includes(searchTerm);
    });
  }

  private defaultSortAccessor(parent: TParent, sortKey: string): Date | number | string | null | undefined {
    const column = this.parentColumns().find((currentColumn) => currentColumn.id === sortKey);

    if (!column) {
      return (parent as Record<string, Date | number | string | null | undefined>)[sortKey];
    }

    if (column.valueGetter) {
      return column.valueGetter(parent);
    }

    return (parent as Record<string, Date | number | string | null | undefined>)[sortKey];
  }

  private getFilterValue<TValue>(
    column: IGroupedDatatableColumn<TValue>,
    value: TValue,
  ): Date | number | string | null | undefined {
    if (column.valueGetter) {
      return column.valueGetter(value);
    }

    return (value as Record<string, Date | number | string | null | undefined>)[column.id];
  }

  private getGroupKey(groupId: idType): string {
    return String(groupId);
  }

  private mergeColumnsWithTemplate<TRow>(
    columns: IGroupedDatatableColumn<TRow>[],
    isParent: boolean,
  ): IColumnWithTemplate<TRow>[] {
    const customColumns = isParent ? this.customParentColumnDef() : this.customChildColumnDef();

    return columns.map((column) => ({
      ...column,
      template: customColumns.find((customColumn) => customColumn.id === column.id)?.template,
    }));
  }

  private setPageState(nextState: Partial<IGroupedDatatablePageChange>): void {
    const currentState = this.pageState();
    const nextPageIndex = nextState.pageIndex ?? currentState.pageIndex;
    const nextPageSize = nextState.pageSize ?? currentState.pageSize;

    if (nextPageIndex === currentState.pageIndex && nextPageSize === currentState.pageSize) {
      return;
    }

    this.pageState.set({
      pageIndex: nextPageIndex,
      pageSize: nextPageSize,
    });
  }

  private setSortState(nextState: Sort): void {
    const currentState = this.internalSortState();

    if (currentState.active === nextState.active && currentState.direction === nextState.direction) {
      return;
    }

    this.internalSortState.set({
      active: nextState.active,
      direction: nextState.direction,
    });
  }

  private areExpansionStatesEqual(leftState: Record<string, boolean>, rightState: Record<string, boolean>): boolean {
    const leftKeys = Object.keys(leftState);
    const rightKeys = Object.keys(rightState);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    for (const key of leftKeys) {
      if (leftState[key] !== rightState[key]) {
        return false;
      }
    }

    return true;
  }
}
